
import { Business as Building2, Event as Calendar, Place as MapPin, People as Users, Close as X } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useEntitySettings } from '@/features/shared/hooks/use-entity-settings';
import type { Character, EntitySummary, Event, EventSummary, Location, Organization } from '@/lib/types';


interface EventDetailsDialogProps {
    novelId: string | null;
    event: Event | EventSummary | null; //TODO: can be any of those? shouldn't be only EventSummary
    open: boolean;
    onOpenChange: (open: boolean) => void;
    locations?: (Location | EntitySummary)[] | null;
    characters?: (Character | EntitySummary)[] | null;
    organizations?: (Organization | EntitySummary)[] | null;
}

export function EventDetailsDialog({
    event,
    open,
    onOpenChange,
    locations,
    characters,
    organizations
}: EventDetailsDialogProps) {
    // Use canonical hook to get settings
    const { sections } = useEntitySettings('events');

    return (
        <Dialog
            open={open}
            onClose={() => onOpenChange(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Calendar color="primary" />
                <Typography variant="h6" fontWeight={700}>
                    {event?.name || 'Event Details'}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => onOpenChange(false)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <X sx={{ fontSize: 20 }} />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {event ? (
                    <Stack spacing={3} sx={{ py: 1 }}>
                        {event.date && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                                <Calendar sx={{ fontSize: 16 }} />
                                <Typography variant="body2">{event.date}</Typography>
                            </Box>
                        )}

                        {('description' in event && event.description) && (
                            <Box>
                                <Typography variant="overline" color="text.secondary" fontWeight={700}>Description</Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{(event as Event).description}</Typography>
                            </Box>
                        )}

                        {event.shortDescription && (
                            <Box>
                                <Typography variant="overline" color="text.secondary" fontWeight={700}>Summary</Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{event.shortDescription}</Typography>
                            </Box>
                        )}

                        {/* Show significance only if it's a full event object and has it */}
                        {('whyItMatters' in event && event.whyItMatters) && (
                            <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 1.5, opacity: 0.9 }}>
                                <Typography variant="subtitle2" component="div" sx={{ mb: 0.5, fontWeight: 700 }}>
                                    Significance
                                </Typography>
                                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{(event as Event).whyItMatters}</Typography>
                            </Box>
                        )}

                        {('details' in event && event.details) && sections.map((section) => {
                            const details = event.details as Record<string, string>;
                            const sectionFieldsWithValues = section.fields.filter((f) => details[f.id]);
                            if (sectionFieldsWithValues.length === 0) return null;

                            return (
                                <Box key={section.id}>
                                    <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ display: 'block', borderBottom: '1px solid', borderColor: 'divider', mb: 1 }}>
                                        {section.title}
                                    </Typography>
                                    <Stack spacing={2}>
                                        {sectionFieldsWithValues.map((field) => (
                                            <Box key={field.id}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>{field.label}</Typography>
                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{details[field.id]}</Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            );
                        })}

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                            {event.locationIds && event.locationIds.length > 0 && (
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <MapPin sx={{ fontSize: 16 }} />
                                        <Typography variant="subtitle2" fontWeight={600}>Locations</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {event.locationIds.map((id: string) => {
                                            const loc = locations?.find(l => l.id === id);
                                            return loc ? <Chip key={id} label={loc.name} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} /> : null;
                                        })}
                                    </Box>
                                </Box>
                            )}

                            {event.characterIds && event.characterIds.length > 0 && (
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Users sx={{ fontSize: 16 }} />
                                        <Typography variant="subtitle2" fontWeight={600}>Characters</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {event.characterIds.map((id: string) => {
                                            const char = characters?.find(c => c.id === id);
                                            return char ? <Chip key={id} label={char.name} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} /> : null;
                                        })}
                                    </Box>
                                </Box>
                            )}

                            {event.organizationIds && event.organizationIds.length > 0 && (
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Building2 sx={{ fontSize: 16 }} />
                                        <Typography variant="subtitle2" fontWeight={600}>Organizations</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {event.organizationIds.map((id: string) => {
                                            const org = organizations?.find(o => o.id === id);
                                            return org ? <Chip key={id} label={org.name} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} /> : null;
                                        })}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Stack>
                ) : (
                    <Box sx={{ py: 8, textAlign: 'center', color: 'text.secondary' }}>
                        No event details found.
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}
